/*
 * Collection Quick Add (buy-box block)
 * ---------------------------------------------------------------------------
 * Lets a customer set quantities for a curated list of related variants and
 * add them all to the cart in one go, without leaving the product page.
 * Mirrors the theme's own multi-item add pattern (see
 * products-bundle-selection.js): builds an items[] array, POSTs to
 * cart_add_url, then refreshes the cart drawer via the cartUpdate pubsub event.
 *
 * Adds ONLY the related rows with qty > 0. The current product is added via
 * the normal Add to Cart button (Approach A).
 */
if (!customElements.get('collection-quick-add')) {
  customElements.define(
    'collection-quick-add',
    class CollectionQuickAdd extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('.cqa__row'));
        this.addBtn = this.querySelector('[data-cqa-add]');
        this.totalEl = this.querySelector('[data-cqa-total]');
        this.errorEl = this.querySelector('.cqa__error');
        this.itemCount = 0;

        this.addEventListener('click', this.onStep.bind(this));
        this.addEventListener('click', this.onToggle.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        if (this.addBtn) this.addBtn.addEventListener('click', this.onAdd.bind(this));

        this.updateTotal();
      }

      onToggle(event) {
        const btn = event.target.closest('[data-cqa-toggle]');
        if (!btn) return;
        event.preventDefault();
        const group = btn.closest('.cqa__group');
        const rest = group && group.querySelector('[data-cqa-rest]');
        const willExpand = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(willExpand));
        if (rest) rest.hidden = !willExpand;
        const textEl = btn.querySelector('.cqa__toggle-text');
        if (textEl) textEl.textContent = willExpand ? btn.dataset.less : btn.dataset.more;
      }

      onStep(event) {
        const minus = event.target.closest('[data-cqa-minus]');
        const plus = event.target.closest('[data-cqa-plus]');
        if (!minus && !plus) return;
        event.preventDefault();
        const row = event.target.closest('.cqa__row');
        const input = row && row.querySelector('[data-cqa-qty]');
        if (!input) return;
        let val = parseInt(input.value, 10) || 0;
        if (plus) val += 1;
        if (minus) val = Math.max(0, val - 1);
        input.value = val;
        this.updateTotal();
      }

      onInput(event) {
        if (event.target.matches('[data-cqa-qty]')) {
          let val = parseInt(event.target.value, 10);
          if (isNaN(val) || val < 0) val = 0;
          event.target.value = val;
          this.updateTotal();
        }
      }

      updateTotal() {
        let total = 0;
        let count = 0;
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-cqa-qty]');
          const qty = input ? parseInt(input.value, 10) || 0 : 0;
          const price = parseInt(row.dataset.price, 10) || 0;
          total += qty * price;
          count += qty;
        });
        this.itemCount = count;
        if (this.totalEl && window.FoxTheme && FoxTheme.Currency) {
          this.totalEl.innerHTML = FoxTheme.Currency.formatMoney(total, FoxTheme.settings.moneyFormat);
        }
        if (this.addBtn) this.addBtn.disabled = count === 0;
      }

      onAdd() {
        if (this.itemCount === 0) return;

        const items = [];
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-cqa-qty]');
          const qty = input ? parseInt(input.value, 10) || 0 : 0;
          if (qty > 0) {
            items.push({ id: parseInt(row.dataset.variantId, 10), quantity: qty });
          }
        });
        if (!items.length) return;

        this.showError(false);
        this.addBtn.classList.add('btn--loading');
        this.addBtn.setAttribute('aria-disabled', 'true');

        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
        );

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items: items,
          sections: sections,
          sections_url: window.location.pathname,
        });

        fetch(`${FoxTheme.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showError(parsedState.description);
              return;
            }
            const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

            this.reset();

            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.show === 'function') drawer.show(this.addBtn);
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            this.addBtn.classList.remove('btn--loading');
            this.addBtn.removeAttribute('aria-disabled');
            this.updateTotal();
          });
      }

      showError(message) {
        if (!this.errorEl) {
          if (message) alert(message);
          return;
        }
        this.errorEl.hidden = !message;
        if (message) this.errorEl.textContent = message;
      }

      reset() {
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-cqa-qty]');
          if (input) input.value = 0;
        });
        this.updateTotal();
      }
    }
  );
}
